import IndicatorButton from 'components/indicator/IndicatorButton';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { FormProvider } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import WsModalContentConfirm from 'pages/ws/components/modalContentConfirm/WsModalContentConfirm';
import WsModalContentPublish from 'pages/ws/components/modalContentPublish/WsModalContentPublish';
import WsContentEditBase from 'pages/ws/components/contentEditBase/WsContentEditBase';
import WsContentEditDesign from 'pages/ws/components/contentEditDesign/WsContentEditDesign';
import WsContentEditPopupEmbed from 'pages/ws/components/contentEditPopupEmbed/WsContentEditPopupEmbed';
import WsContentEditReport from 'pages/ws/components/contentEditReport/WsContentEditReport';
import WsContentEditRespondentFields from 'pages/ws/components/contentEditRespondentFields/WsContentEditRespondentFields';
import WsQuizEditChatbot from 'pages/ws/quizEdit/components/quizEditChatbot/WsQuizEditChatbot';
import WsQuizEditQuestions from 'pages/ws/quizEdit/components/quizEditQuestions/WsQuizEditQuestions';
import WsQuizEditResults from 'pages/ws/quizEdit/components/quizEditResults/WsQuizEditResults';
import WsQuizEditStatusInfo from 'pages/ws/quizEdit/components/quizEditStatusInfo/WsQuizEditStatusInfo';
import { useWsQuizEdit } from 'pages/ws/quizEdit/hooks/useWsQuizEdit';

import type { JSX } from 'react';

/**
 * クイズ作成・編集画面
 * ロジックは useWsQuizEdit に委譲し、JSX のみを担当する。
 */
export default function WsQuizEdit(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const {
    useFormMethods,
    questionFieldArray,
    resultFieldArray,
    respondentFieldArray,
    initialized,
    processingOnSave,
    processingOnPreview,
    processingOnPublish,
    processingOnPublishTerminate,
    processingOnReserveCancel,
    processingOnDelete,
    processingOnCreateQuestion,
    quizState,
    isExpiredPlan,
    canPublished,
    isUnpublished,
    isReadOnly,
    modalPublishOpenState,
    setModalPublishOpenState,
    modalConfirmOpenState,
    setModalConfirmOpenState,
    modalConfirmTitle,
    onSave,
    onPreview,
    onPublishModalOpen,
    onConfirmModalSubmit,
    onPublishModalSubmit,
    onPublishTerminate,
    onReserveCancel,
    onDelete,
    onCreateQuizRef,
    navigate,
    confirm,
  } = useWsQuizEdit();

  const { setValue, getValues } = useFormMethods;

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <FormProvider {...useFormMethods}>
        {/* 初期化中の表示 */}
        <IndicatorScreen visible={!initialized || processingOnPublish} />
        {/* 初期化後の表示 */}
        {initialized && (
          <>
            <section className="section-header">
              <div className="container">
                <a onClick={() => navigate(-1)} className="btn-back-list mt-0 mb-3">
                  戻る
                </a>
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left">
                    <h2 className="flex-title">クイズ作成・編集</h2>
                  </div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      {/* 保存ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onSave}
                            disabled={processingOnSave}
                            className="btn btn-common green w-100"
                          >
                            <IndicatorButton visible={processingOnSave} />
                            保存する
                          </button>
                        </li>
                      )}

                      {/* 公開停止ボタン */}
                      {quizState?.quizStatusDiv == DivConst.items.quizStatus.published.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onPublishTerminate}
                            disabled={processingOnPublishTerminate}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnPublishTerminate} />
                            公開を停止する
                          </button>
                        </li>
                      )}
                      {/* 予約停止ボタン */}
                      {quizState?.quizStatusDiv == DivConst.items.quizStatus.reserved.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onReserveCancel}
                            disabled={processingOnReserveCancel}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnReserveCancel} />
                            予約を取り消す
                          </button>
                        </li>
                      )}
                      {/* プレビューボタン */}
                      <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                        <button
                          onClick={onPreview}
                          disabled={processingOnPreview}
                          className="btn btn-common outline-blue w-100"
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          {`${isUnpublished() ? '保存して' : ''}プレビュー`}
                        </button>
                      </li>
                      {/* 公開ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-lg ms-lg-4 mb-2">
                          <div
                            data-tooltip-id="custom-tooltip"
                            data-tooltip-content={
                              isExpiredPlan
                                ? 'プランの期限がきれました。プランのアップグレードをご検討ください。'
                                : 'トライアルプランの公開は1つまでです。他のクイズを一度非公開にするかプランのアップグレードをご検討ください。'
                            }
                            className="tooltip-wrapper"
                          >
                            <button
                              onClick={onPublishModalOpen}
                              disabled={isExpiredPlan || !canPublished}
                              className="btn btn-common moderate-blue w-100"
                            >
                              公開する
                            </button>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                {/* 削除ボタン */}
                {isUnpublished() && quizState && (
                  <div className="d-md-flex d-flex-header mt-2">
                    <div className="flex-shrink-0 flex-left"></div>
                    <div className="flex-grow-1 flex-right">
                      <ul className="list-inline list-btn mb-0">
                        <li className="list-inline-item list-item-lg ms-lg-4 pe-1">
                          <a
                            onClick={() =>
                              confirm({
                                title: 'クイズの削除',
                                message:
                                  'クイズや獲得リストなどの関連データを全て削除します。この操作は取り消せません。',
                                onSubmit: onDelete,
                              })
                            }
                            className="text-danger"
                          >
                            <IndicatorButton visible={processingOnDelete} />
                            削除する
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* ステータスや公開情報の表示 */}
                <WsQuizEditStatusInfo quiz={quizState} />
              </div>
            </section>

            <section className="section-header"></section>

            {/* 基本設定 */}
            <div data-section="basic">
              <WsContentEditBase
                readOnly={isReadOnly()}
                contentCategory={DivConst.items.contentsType.quiz}
                onCreate={() => onCreateQuizRef.current?.()}
                processing={processingOnCreateQuestion}
              />
            </div>
            {/* 取得項目設定 */}
            <WsContentEditRespondentFields
              readOnly={isReadOnly()}
              contentCategory={DivConst.items.contentsType.quiz}
              respondentFieldArray={respondentFieldArray}
            />
            {/* デザイン設定 */}
            <WsContentEditDesign
              readOnly={isReadOnly()}
              contentCategory={DivConst.items.contentsType.quiz}
            />
            {/* 設問設定 */}
            <div data-section="questions">
              <WsQuizEditQuestions readOnly={isReadOnly()} fieldArray={questionFieldArray} />
            </div>
            {/* 判定結果 */}
            <div data-section="results">
              <WsQuizEditResults readOnly={isReadOnly()} fieldArray={resultFieldArray} />
            </div>
            {/* レポートメール設定 */}
            <WsContentEditReport
              readOnly={isReadOnly()}
              contentCategory={DivConst.items.contentsType.quiz}
            />
            {/* ポップアップ埋め込みタグ */}
            <WsContentEditPopupEmbed readOnly={isReadOnly()} quizState={quizState} />

            <section className="section-header">
              <div className="container">
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left"></div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      {/* 保存ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onSave}
                            disabled={processingOnSave}
                            className="btn btn-common green w-100"
                          >
                            <IndicatorButton visible={processingOnSave} />
                            保存する
                          </button>
                        </li>
                      )}

                      {/* 公開停止ボタン */}
                      {quizState?.quizStatusDiv == DivConst.items.quizStatus.published.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onPublishTerminate}
                            disabled={processingOnPublishTerminate}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnPublishTerminate} />
                            公開を停止する
                          </button>
                        </li>
                      )}
                      {/* 予約停止ボタン */}
                      {quizState?.quizStatusDiv == DivConst.items.quizStatus.reserved.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onReserveCancel}
                            disabled={processingOnReserveCancel}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnReserveCancel} />
                            予約を取り消す
                          </button>
                        </li>
                      )}
                      {/* プレビューボタン */}
                      <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                        <button
                          onClick={onPreview}
                          disabled={processingOnPreview}
                          className="btn btn-common outline-blue w-100"
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          {`${isUnpublished() ? '保存して' : ''}プレビュー`}
                        </button>
                      </li>
                      {/* 公開ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-lg ms-lg-4 mb-2">
                          <div
                            data-tooltip-id="custom-tooltip"
                            data-tooltip-content={
                              isExpiredPlan
                                ? 'プランの期限がきれました。プランのアップグレードをご検討ください。'
                                : 'トライアルプランの公開は1つまでです。他のクイズを一度非公開にするかプランのアップグレードをご検討ください。'
                            }
                            className="tooltip-wrapper"
                          >
                            <button
                              onClick={onPublishModalOpen}
                              disabled={isExpiredPlan || !canPublished}
                              className="btn btn-common moderate-blue w-100"
                            >
                              公開する
                            </button>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </FormProvider>

      {/* クイズ公開設定モーダル */}
      <WsModalContentPublish
        open={modalPublishOpenState}
        setOpen={setModalPublishOpenState}
        publishStartAt={quizState?.publishStartAt}
        publishEndAt={quizState?.publishEndAt}
        noIndexFlag={quizState?.noIndexFlag}
        onSubmit={attr => onPublishModalSubmit(attr)}
      />
      {
        // トライアルプラン（利用期間切れ）もしくは公開上限の場合以外は表示しない
        (isExpiredPlan || !canPublished) && (
          <Tooltip id="custom-tooltip" place="top" variant="info" />
        )
      }

      {/* 未設定確認モーダル */}
      <WsModalContentConfirm
        open={modalConfirmOpenState}
        setOpen={setModalConfirmOpenState}
        title={modalConfirmTitle}
        onSubmit={onConfirmModalSubmit}
      />
      {/* チャットボットコンポーネント */}
      <WsQuizEditChatbot
        getValues={getValues}
        setValue={setValue}
        questionFieldArray={questionFieldArray}
        resultFieldArray={resultFieldArray}
        onCreateRef={onCreateQuizRef}
      />
    </>
  );
}
